import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import useClickAway from "@/core/helpers/useClickAway";
import { BankCardIcon, CashIcon, RubleIcon } from "@/shared/icons";
import { Button } from "@/shared/ui";
import CloseButton from "@/shared/ui/CloseButton";
import Loader from "@/shared/ui/Loader";

type Props = {
  name: string;
  address: string;
  price: number;
  onClose: () => void;
  onConfirm: (type: PaymentType) => void;
  isLoading?: boolean;
  isConfirming: boolean;
};

export default function BusinessBuyModal({
  name,
  address,
  price,
  onClose,
  onConfirm,
  isLoading,
  isConfirming,
}: Props) {
  const { t } = useTranslation("business");

  const ref = useClickAway(onClose);

  return (
    <div
      className={clsx(
        "absolute z-[60] top-0 left-0 right-0 bottom-0 bg-[#0F0F0F]/95",
        "flex items-center justify-center",
        "animate-[--animation-fade-in]",
      )}
      data-mz-accent="lightBlue"
    >
      {isLoading && <Loader />}

      {!isLoading && (
        <div
          ref={ref}
          className={clsx(
            "w-[25.25rem] p-5 bg-gradient-to-b from-[#0F0F0F] to-[#0F0F0F]/90 rounded-[0.875rem]",
            "relative p-7.5 flex flex-col items-center lightBlueShadow",
          )}
        >
          <CloseButton onClose={onClose} className="absolute top-2.5 right-2.5 size-7.5" />
          <div className="text-18 font-bold text-white">{name}</div>
          <div className="text-15 font-medium text-white/70 mt-1">{address}</div>
          <div className="h-px w-[20rem] bg-[#1F1F1F]/80 flex justify-center my-5">
            <div className="h-full w-[5rem] bg-accent-solid" />
          </div>
          <p className="max-w-72 mb-5 text-16 font-medium text-center">
            {t("business.confirmSaleQuestion")}
          </p>
          <div
            className={clsx(
              "flex items-center gap-2 justify-center w-full h-11 mb-3.5",
              "rounded-[0.5rem] border border-dashed border-accent-solid",
              "bg-gradient-to-r from-[#007FDB]/20 to-[#0164AC]/20",
            )}
          >
            <RubleIcon className="size-5.5 text-accent-solid" />
            <div className="text-16 text-white font-bold">{formatPrice(price)} ₽</div>
          </div>
          <div className="grid grid-cols-2 gap-2 w-full">
            <Button
              className="gap-2.5 h-11"
              onClick={() => onConfirm(PaymentType.Card)}
              loading={isConfirming}
              disabled={isConfirming}
            >
              <BankCardIcon className="size-4.5" />
              {t("business.purchaseType.withCard")}
            </Button>
            <Button
              className="gap-2.5 h-11"
              onClick={() => onConfirm(PaymentType.Cash)}
              loading={isConfirming}
              disabled={isConfirming}
            >
              <CashIcon className="size-4.5" />
              {t("business.purchaseType.withCash")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
